import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { managedDeviceTypesApi } from '../services/managedDeviceTypesApi';

/**
 * Hook for managing device types
 */
export const useManagedDeviceTypes = (includeInactive = false) => {
  return useQuery({
    queryKey: ['managedDeviceTypes', { includeInactive }],
    queryFn: () => managedDeviceTypesApi.getAll(includeInactive),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for getting a specific device type by slug
 */
export const useManagedDeviceType = (slug) => {
  return useQuery({
    queryKey: ['managedDeviceType', slug],
    queryFn: () => managedDeviceTypesApi.getBySlug(slug),
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook for getting kinds of a specific device type
 */
export const useManagedDeviceTypeKinds = (typeSlug) => {
  return useQuery({
    queryKey: ['managedDeviceTypeKinds', typeSlug],
    queryFn: () => managedDeviceTypesApi.getKindsByTypeSlug(typeSlug),
    enabled: !!typeSlug,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook for creating a new device type
 */
export const useCreateManagedDeviceType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: managedDeviceTypesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managedDeviceTypes'] });
    },
  });
};

/**
 * Hook for updating a device type
 */
export const useUpdateManagedDeviceType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => managedDeviceTypesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managedDeviceTypes'] });
      queryClient.invalidateQueries({ queryKey: ['managedDeviceType'] });
    },
  });
};

/**
 * Hook for deleting a device type
 */
export const useDeleteManagedDeviceType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: managedDeviceTypesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managedDeviceTypes'] });
    },
  });
};